import React, { useState } from 'react';
import { Search, HelpCircle, BookOpen, FileText, Mail, Phone, Star, ArrowRight, ExternalLink } from 'lucide-react';

const ProjectManagementSupport = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Sample knowledge base data
  const knowledgeBase = [
    {
      id: 1,
      title: "Creating your first project timeline",
      category: "Getting Started",
      tags: ["timeline", "gantt", "project setup"]
    },
    {
      id: 2,
      title: "Managing team permissions and roles",
      category: "Team Management",
      tags: ["permissions", "roles", "access control"]
    },
    {
      id: 3,
      title: "Setting up automated reports",
      category: "Reporting",
      tags: ["automation", "reports", "dashboard"]
    },
    {
      id: 4,
      title: "Integrating with external tools",
      category: "Integrations",
      tags: ["API", "third-party", "connect"]
    },
    {
      id: 5,
      title: "Resource allocation best practices",
      category: "Resources",
      tags: ["allocation", "capacity", "planning"]
    }
  ];
  
  // Handle search
  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      const results = knowledgeBase.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      setSearchResults(results);
      setIsSearching(false);
    }, 500);
  };
  
  // Support categories
  const supportCategories = [
    {
      icon: <BookOpen size={24} className="text-pink-500" />,
      title: "Documentation",
      description: "Browse comprehensive guides and resources",
      backgroundColor: "bg-pink-50",
      borderColor: "border-pink-300",
      iconBackground: "bg-pink-100",
      hoverColor: "hover:bg-pink-100"
    },
    {
      icon: <FileText size={24} className="text-amber-500" />,
      title: "Tutorials",
      description: "Step-by-step instructions for common tasks",
      backgroundColor: "bg-amber-50",
      borderColor: "border-amber-300",
      iconBackground: "bg-amber-100",
      hoverColor: "hover:bg-amber-100"
    },
    {
      icon: <HelpCircle size={24} className="text-emerald-500" />,
      title: "FAQ",
      description: "Quick answers to common questions",
      backgroundColor: "bg-emerald-50",
      borderColor: "border-emerald-300",
      iconBackground: "bg-emerald-100",
      hoverColor: "hover:bg-emerald-100"
    }
  ];
  
  // Featured articles
  const featuredArticles = [
    {
      title: "Creating effective project milestones",
      views: "5.2k",
      timeToRead: "6 min"
    },
    {
      title: "Task dependency management guide",
      views: "3.8k",
      timeToRead: "8 min"
    },
    {
      title: "Resource allocation best practices",
      views: "4.5k",
      timeToRead: "5 min"
    },
    {
      title: "Team collaboration techniques",
      views: "6.1k",
      timeToRead: "7 min"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with search */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 pt-20 pb-24">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Help & Support Center</h1>
          <p className="text-white text-opacity-90 text-xl max-w-3xl mb-12">
            Find answers, tutorials, and expert guidance for your project management needs
          </p>
          
          {/* Search functionality */}
          <div className="relative max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search size={20} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search for help topics, tutorials, FAQs..."
              className="w-full pl-12 pr-20 py-4 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="absolute right-3 top-2.5 bg-indigo-800 hover:bg-indigo-900 text-white px-4 py-1.5 rounded-lg transition-colors"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="bg-white mt-4 rounded-xl shadow-lg p-3 max-w-2xl">
              <div className="text-sm text-gray-500 mb-2 px-3">
                Found {searchResults.length} results for "{searchTerm}"
              </div>
              
              <div className="space-y-1">
                {searchResults.map(result => (
                  <div 
                    key={result.id}
                    className="p-3 hover:bg-indigo-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between">
                      <h3 className="font-medium text-indigo-700">{result.title}</h3>
                      <span className="text-xs font-medium bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">
                        {result.category}
                      </span>
                    </div>
                    <div className="flex mt-1">
                      {result.tags.map((tag, i) => (
                        <span key={i} className="text-xs text-gray-500 mr-2">#{tag}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main content */}
      <div className="max-w-5xl mx-auto px-6 -mt-16 pb-24">
        {/* Support categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {supportCategories.map((category, index) => (
            <div 
              key={index}
              className={`${category.backgroundColor} border ${category.borderColor} rounded-xl shadow-md overflow-hidden transition-all ${category.hoverColor} hover:shadow-lg`}
            >
              <div className="p-6">
                <div className={`w-12 h-12 rounded-full ${category.iconBackground} flex items-center justify-center mb-4`}>
                  {category.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{category.title}</h3>
                <p className="text-gray-600 mb-4">{category.description}</p>
                <button className="flex items-center text-indigo-600 hover:text-indigo-800 font-medium">
                  Browse {category.title.toLowerCase()} <ArrowRight size={16} className="ml-2" />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Featured articles */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Featured Articles</h2>
            <button className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center">
              View all <ExternalLink size={16} className="ml-1" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {featuredArticles.map((article, index) => (
              <div 
                key={index}
                className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start">
                  <div className="text-indigo-500 mr-3 mt-1">
                    <Star size={16} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">{article.title}</h3>
                    <div className="flex text-sm text-gray-500">
                      <span className="mr-4">{article.views} views</span>
                      <span>{article.timeToRead} read</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Contact support */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200 shadow-md">
            <div className="mb-4">
              <Mail size={28} className="text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Email Support</h3>
            <p className="text-gray-600 mb-4">
              Submit a support ticket and our team will get back to you within 24 hours.
            </p>
            <button className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-5 rounded-lg transition-colors">
              Submit a ticket
            </button>
          </div>
          
          <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-6 border border-pink-200 shadow-md">
            <div className="mb-4">
              <Phone size={28} className="text-pink-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Live Support</h3>
            <p className="text-gray-600 mb-4">
              Speak directly with our support specialists for urgent assistance.
            </p>
            <button className="bg-pink-600 hover:bg-pink-700 text-white font-medium py-2 px-5 rounded-lg transition-colors">
              Start live chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectManagementSupport;